// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable @typescript-eslint/no-misused-promises */
// Added to allow async functions passed to apple pay session callbacks

import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'

import { PartialAddress } from '@src/quince-site-common/src/services/addressService/addressType'
import {
  CheckoutExpressUpdateDetail,
  EnrichedCheckout,
  ShippingMethods,
} from '@src/quince-site-common/src/services/checkout-service-full/checkout-service-full.types'
import { handleApplePayPaymentFailedTracking } from '@src/quince-site-common/src/trackers/helpers/handleApplePayTracking'
import { CHECKOUT_API_NAMES, CHECKOUT_ERROR_STAGES } from '@src/telemetry/checkout-error.helper'
import {
  capturePdpExpressError,
  PDP_EXPRESS_ACTIONS,
  PDP_EXPRESS_APPLE_PAY,
  PDP_EXPRESS_SHIPPING_UPDATE_TYPES,
  PDP_EXPRESS_STEPS,
  trackPdpExpressAction,
} from '@src/telemetry/pdp-express-rum.helper'
import {
  initializeUnifiedPayButtonClick,
  trackCheckoutErrorLog,
  trackUnifiedPayButtonClick,
} from '@src/telemetry/track-unified-checkout-events'

import {
  completeShippingContactSelectionWithError,
  createApplePaySession,
  getPartialAddress,
  handlePaymentAuthorization,
  initializeApplePay,
  makeApplePayBraintreeShippingMethods,
  makeBraintreeLineItems,
  makeCheckoutExpressUpdateDetail,
  OnPaymentAuthorizedEvent,
  OnShippingContactSelectedEvent,
  OnShippingMethodSelectedEvent,
  TRACKING_PAYMENT_GATEWAY_BRAINTREE,
  UpdateExpressCompleteDetails,
} from './apple-pay-button.helpers'

import type { ApplePay, ApplePayStatusCodes } from 'braintree-web'

const BRAINTREE_LINE_ITEM_DEFAULT_TYPE_FINAL = 'final'
const TRACKING_FLOW = 'Checkout from PDP'
const TRACKING_PAYMENT_METHOD = 'Apple Pay'

interface UseApplePayBraintreeProps {
  onError?: (error?: unknown) => void
  getCheckoutDetails: (isPreorder?: boolean) => Promise<EnrichedCheckout | null>
  updateShippingAddress: (address: PartialAddress) => Promise<CheckoutExpressUpdateDetail>
  updateShippingOption: (shippingOptionId: ShippingMethods) => Promise<CheckoutExpressUpdateDetail>
  validateApplePayClick?: () => boolean
  productPrice?: number
  checkoutExpressUpdateDetail: CheckoutExpressUpdateDetail | null
  updateExpressCompleteDetails: UpdateExpressCompleteDetails
  capture: () => Promise<void>
  skipEmail?: boolean
  getZeroAmountCheckoutUrl?: () => string | null
  showZeroAmountCheckoutModal?: () => void
}

export const useApplePayBraintree = ({
  onError,
  getCheckoutDetails,
  validateApplePayClick,
  productPrice,
  updateShippingAddress,
  updateShippingOption,
  checkoutExpressUpdateDetail,
  updateExpressCompleteDetails,
  capture,
  skipEmail = false,
  getZeroAmountCheckoutUrl,
  showZeroAmountCheckoutModal,
}: UseApplePayBraintreeProps) => {
  const { t } = useTranslation('common')
  const [applePayInstance, setApplePayInstance] = useState<ApplePay | null>(null)
  const [isSupported, setIsSupported] = useState(false)
  const [isApplePayClickInProgress, setIsApplePayClickInProgress] = useState(false)
  const checkoutExpressUpdateDetailRef = useRef<CheckoutExpressUpdateDetail | null>(checkoutExpressUpdateDetail)

  useEffect(() => {
    checkoutExpressUpdateDetailRef.current = checkoutExpressUpdateDetail
  }, [checkoutExpressUpdateDetail])

  useEffect(() => {
    const setupApplePay = async () => {
      try {
        const { applePayInstance, isSupported } = await initializeApplePay(onError)
        setIsSupported(isSupported)
        setApplePayInstance(applePayInstance)
      } catch (err) {
        console.error('Error creating Braintree ApplePay instance:', err)
      }
    }

    void setupApplePay()
  }, [onError])

  const handleApplePayClick = () => {
    if (!applePayInstance) return
    if (isApplePayClickInProgress) return

    try {
      setIsApplePayClickInProgress(true)
      if (!window.ApplePaySession) {
        trackUnifiedPayButtonClick({
          flow: TRACKING_FLOW,
          paymentMethod: TRACKING_PAYMENT_METHOD,
          errorReason: 'apple_pay_unsupported',
        })
        trackPdpExpressAction(
          PDP_EXPRESS_ACTIONS.PAY_BUTTON_CLICKED,
          PDP_EXPRESS_APPLE_PAY,
          TRACKING_PAYMENT_GATEWAY_BRAINTREE,
          { error_reason: 'apple_pay_unsupported' },
        )
        capturePdpExpressError(
          new Error('apple_pay_unsupported'),
          PDP_EXPRESS_STEPS.APPLE_PAY_UNSUPPORTED,
          PDP_EXPRESS_APPLE_PAY,
          TRACKING_PAYMENT_GATEWAY_BRAINTREE,
        )
        onError?.(new Error('Apple Pay is not supported'))
        setIsApplePayClickInProgress(false)
        return
      }

      // Synchronous validations to check if payment is possible
      const isValid = validateApplePayClick?.()
      if (!isValid) {
        trackUnifiedPayButtonClick({
          flow: TRACKING_FLOW,
          paymentMethod: TRACKING_PAYMENT_METHOD,
          errorReason: 'validation_failed',
        })
        trackPdpExpressAction(
          PDP_EXPRESS_ACTIONS.PAY_BUTTON_CLICKED,
          PDP_EXPRESS_APPLE_PAY,
          TRACKING_PAYMENT_GATEWAY_BRAINTREE,
          { error_reason: 'validation_failed' },
        )
        capturePdpExpressError(
          new Error('validation_failed'),
          PDP_EXPRESS_STEPS.VALIDATION_FAILED,
          PDP_EXPRESS_APPLE_PAY,
          TRACKING_PAYMENT_GATEWAY_BRAINTREE,
        )
        setIsApplePayClickInProgress(false)
        return
      }

      const checkoutDetailsPromise = getCheckoutDetails()
      void checkoutDetailsPromise.catch(() => {
        // The Apple Pay session handlers consume this promise later.
      })
      const initialDetail = makeCheckoutExpressUpdateDetail(undefined, undefined, productPrice)
      const session = createApplePaySession(applePayInstance, initialDetail, true, t, skipEmail)

      if (!session) {
        trackUnifiedPayButtonClick({
          flow: TRACKING_FLOW,
          paymentMethod: TRACKING_PAYMENT_METHOD,
          errorReason: 'session_creation_failed',
        })
        trackPdpExpressAction(
          PDP_EXPRESS_ACTIONS.PAY_BUTTON_CLICKED,
          PDP_EXPRESS_APPLE_PAY,
          TRACKING_PAYMENT_GATEWAY_BRAINTREE,
          { error_reason: 'session_creation_failed' },
        )
        capturePdpExpressError(
          new Error('session_creation_failed'),
          PDP_EXPRESS_STEPS.SESSION_CREATION,
          PDP_EXPRESS_APPLE_PAY,
          TRACKING_PAYMENT_GATEWAY_BRAINTREE,
        )
        onError?.(new Error('Unable to create Apple Pay session'))
        setIsApplePayClickInProgress(false)
        return
      }

      initializeUnifiedPayButtonClick({
        flow: TRACKING_FLOW,
        paymentMethod: TRACKING_PAYMENT_METHOD,
        paymentGateway: TRACKING_PAYMENT_GATEWAY_BRAINTREE,
      })
      trackPdpExpressAction(
        PDP_EXPRESS_ACTIONS.PAY_BUTTON_CLICKED,
        PDP_EXPRESS_APPLE_PAY,
        TRACKING_PAYMENT_GATEWAY_BRAINTREE,
      )

      let hasShippingContactSelectionError = false

      // Completing a selection with null shippingMethods/total/lineItems wedges the Apple Pay
      // session so it never fires again — fall back to the last-known-good checkout detail.
      const getStaleCheckoutFallback = () => {
        const detail = checkoutExpressUpdateDetailRef.current ?? initialDetail
        return {
          shippingMethods: makeApplePayBraintreeShippingMethods(detail, t),
          newTotal: {
            label: 'Quince',
            amount: detail.amountToCollect.toFixed(2),
          },
          lineItems: makeBraintreeLineItems(detail, BRAINTREE_LINE_ITEM_DEFAULT_TYPE_FINAL, true),
        }
      }

      session.onvalidatemerchant = (event: { validationURL: string }) => {
        applePayInstance.performValidation(
          {
            validationURL: event.validationURL,
            displayName: 'Quince',
          },
          (err, merchantSession) => {
            if (err) {
              console.error('Apple Pay validation failed:', err)
              trackCheckoutErrorLog({
                error: err,
                errorReason: err instanceof Error ? err.message : 'merchant_validation_failed',
                stage: CHECKOUT_ERROR_STAGES.MERCHANT_VALIDATION,
              })
              capturePdpExpressError(
                err,
                PDP_EXPRESS_STEPS.MERCHANT_VALIDATION,
                PDP_EXPRESS_APPLE_PAY,
                TRACKING_PAYMENT_GATEWAY_BRAINTREE,
              )
              setIsApplePayClickInProgress(false)
              session.abort()
              onError?.(err)
              return
            }
            session.completeMerchantValidation(merchantSession)
          },
        )
      }

      session.onshippingcontactselected = async (event: OnShippingContactSelectedEvent) => {
        try {
          const checkoutDetails = await checkoutDetailsPromise

          if (!checkoutDetails) {
            setIsApplePayClickInProgress(false)
            session.abort()
            // `getCheckoutDetails` can return null for two distinct reasons:
            // 1. Zero-amount coupon flow: `useExpressPay` stores a checkout URL that
            //    `getZeroAmountCheckoutUrl` exposes, so we open the fallback modal.
            // 2. Mini checkout creation failed: no fallback URL is available, so we
            //    surface the existing checkout creation error.
            if (!getZeroAmountCheckoutUrl?.()) {
              trackCheckoutErrorLog({
                errorReason: 'checkout_creation_failed',
                stage: CHECKOUT_ERROR_STAGES.CHECKOUT_CREATION,
                apiName: CHECKOUT_API_NAMES.CHECKOUT_SERVICE,
              })
              capturePdpExpressError(
                new Error('checkout_creation_failed'),
                PDP_EXPRESS_STEPS.CHECKOUT_CREATION,
                PDP_EXPRESS_APPLE_PAY,
                TRACKING_PAYMENT_GATEWAY_BRAINTREE,
              )
              onError?.(new Error('Failed to create checkout'))
            } else {
              showZeroAmountCheckoutModal?.()
            }
            return
          }
          const { partialAddress, newTotal } = await getPartialAddress(event, updateShippingAddress)
          hasShippingContactSelectionError = false

          session.completeShippingContactSelection(
            window.ApplePaySession?.STATUS_SUCCESS as ApplePayStatusCodes,
            makeApplePayBraintreeShippingMethods(partialAddress, t),
            newTotal,
            makeBraintreeLineItems(partialAddress, BRAINTREE_LINE_ITEM_DEFAULT_TYPE_FINAL, true),
          )
          trackPdpExpressAction(
            PDP_EXPRESS_ACTIONS.SHIPPING_UPDATED,
            PDP_EXPRESS_APPLE_PAY,
            TRACKING_PAYMENT_GATEWAY_BRAINTREE,
            {
              update_type: PDP_EXPRESS_SHIPPING_UPDATE_TYPES.CONTACT,
            },
          )
        } catch (err) {
          console.error('Error updating shipping contact:', err)
          trackCheckoutErrorLog({
            error: err,
            errorReason: err instanceof Error ? err.message : 'shipping_contact_update_failed',
            stage: CHECKOUT_ERROR_STAGES.SHIPPING_CONTACT_UPDATE,
            apiName: CHECKOUT_API_NAMES.CHECKOUT_SERVICE,
          })
          capturePdpExpressError(
            err,
            PDP_EXPRESS_STEPS.SHIPPING_CONTACT_UPDATE,
            PDP_EXPRESS_APPLE_PAY,
            TRACKING_PAYMENT_GATEWAY_BRAINTREE,
          )
          const staleCheckoutFallback = getStaleCheckoutFallback()
          const errorShownOnSheet = completeShippingContactSelectionWithError(
            session,
            t('enter_a_valid_address'),
            staleCheckoutFallback,
          )
          if (errorShownOnSheet) {
            hasShippingContactSelectionError = true
          } else {
            // Can't surface the error on the sheet — close it rather than leave the shopper on a
            // sheet that looks valid while the backend rejected the address.
            setIsApplePayClickInProgress(false)
            session.abort()
            onError?.(err)
          }
        }
      }

      session.onshippingmethodselected = async (event: OnShippingMethodSelectedEvent) => {
        try {
          const updatedShippingOptionData = await updateShippingOption(
            event.shippingMethod.identifier as ShippingMethods,
          )
          const newTotal = {
            label: 'Quince',
            amount: updatedShippingOptionData.amountToCollect.toFixed(2),
          }
          session.completeShippingMethodSelection(
            window.ApplePaySession?.STATUS_SUCCESS as ApplePayStatusCodes,
            newTotal,
            makeBraintreeLineItems(updatedShippingOptionData, BRAINTREE_LINE_ITEM_DEFAULT_TYPE_FINAL, true),
          )
          trackPdpExpressAction(
            PDP_EXPRESS_ACTIONS.SHIPPING_UPDATED,
            PDP_EXPRESS_APPLE_PAY,
            TRACKING_PAYMENT_GATEWAY_BRAINTREE,
            {
              update_type: PDP_EXPRESS_SHIPPING_UPDATE_TYPES.METHOD,
            },
          )
        } catch (err) {
          console.error('Error updating shipping method:', err)
          trackCheckoutErrorLog({
            error: err,
            errorReason: err instanceof Error ? err.message : 'shipping_method_update_failed',
            stage: CHECKOUT_ERROR_STAGES.SHIPPING_METHOD_UPDATE,
            apiName: CHECKOUT_API_NAMES.CHECKOUT_SERVICE,
          })
          capturePdpExpressError(
            err,
            PDP_EXPRESS_STEPS.SHIPPING_METHOD_UPDATE,
            PDP_EXPRESS_APPLE_PAY,
            TRACKING_PAYMENT_GATEWAY_BRAINTREE,
          )
          setIsApplePayClickInProgress(false)
          session.abort()
          onError?.(err)
        }
      }

      session.onpaymentauthorized = async (event: OnPaymentAuthorizedEvent) => {
        if (hasShippingContactSelectionError) {
          session.completePayment(window.ApplePaySession?.STATUS_FAILURE as ApplePayStatusCodes)
          trackCheckoutErrorLog({
            errorReason: 'shipping_update_failed_blocked_authorization',
            stage: CHECKOUT_ERROR_STAGES.SHIPPING_BLOCKED_AUTHORIZATION,
          })
          capturePdpExpressError(
            new Error('shipping_update_failed_blocked_authorization'),
            PDP_EXPRESS_STEPS.SHIPPING_BLOCKED_AUTHORIZATION,
            PDP_EXPRESS_APPLE_PAY,
            TRACKING_PAYMENT_GATEWAY_BRAINTREE,
          )
          setIsApplePayClickInProgress(false)
          onError?.(new Error('Apple Pay shipping update failed'))
          return
        }
        try {
          const latestCheckoutExpressUpdateDetail =
            checkoutExpressUpdateDetailRef.current ||
            checkoutExpressUpdateDetail ||
            makeCheckoutExpressUpdateDetail(await checkoutDetailsPromise, undefined, productPrice)

          await handlePaymentAuthorization(
            event,
            session,
            applePayInstance,
            latestCheckoutExpressUpdateDetail,
            updateExpressCompleteDetails,
            capture,
            skipEmail,
          )
          setIsApplePayClickInProgress(false)
        } catch (err) {
          console.error('Apple Pay payment authorization failed:', err)
          handleApplePayPaymentFailedTracking(err)
          trackCheckoutErrorLog({
            error: err,
            errorReason: err instanceof Error ? err.message : String(err),
            stage: CHECKOUT_ERROR_STAGES.PAYMENT_AUTHORIZATION,
            apiName: CHECKOUT_API_NAMES.UNIFIED_PAYMENT_GATEWAY,
          })
          capturePdpExpressError(
            err,
            PDP_EXPRESS_STEPS.PAYMENT_AUTHORIZATION,
            PDP_EXPRESS_APPLE_PAY,
            TRACKING_PAYMENT_GATEWAY_BRAINTREE,
          )
          setIsApplePayClickInProgress(false)
          onError?.(err)
        }
      }

      session.oncancel = () => {
        setIsApplePayClickInProgress(false)
      }

      try {
        session.begin()
      } catch (beginError) {
        console.error('Error calling session.begin() on apple pay click:', beginError)
        trackCheckoutErrorLog({
          error: beginError,
          errorReason: beginError instanceof Error ? beginError.message : 'session_begin_failed',
          stage: CHECKOUT_ERROR_STAGES.SESSION_BEGIN,
        })
        capturePdpExpressError(
          beginError,
          PDP_EXPRESS_STEPS.SESSION_BEGIN,
          PDP_EXPRESS_APPLE_PAY,
          TRACKING_PAYMENT_GATEWAY_BRAINTREE,
        )
        setIsApplePayClickInProgress(false)
        onError?.(beginError)
      }
    } catch (error) {
      console.error('Apple Pay payment failed:', error)
      trackCheckoutErrorLog({
        error,
        errorReason: error instanceof Error ? error.message : 'apple_pay_click_failed',
        stage: CHECKOUT_ERROR_STAGES.CLICK,
      })
      capturePdpExpressError(error, PDP_EXPRESS_STEPS.CLICK, PDP_EXPRESS_APPLE_PAY, TRACKING_PAYMENT_GATEWAY_BRAINTREE)
      setIsApplePayClickInProgress(false)
      onError?.(error)
    }
  }

  return { applePayInstance, isApplePayClickInProgress, isSupported, handleApplePayClick }
}
